/**
 * Settings Service
 * Handles user settings CRUD operations with offline-first Dexie caching
 */

import { get, put, patch } from '@/lib/api-client';
// `put` is used by updateProfile (PUT /settings/profile) but NOT by a
// full-replace of /settings — we removed that surface because every new
// settings field would otherwise become a required field on PUT and 422 any
// caller that doesn't keep up. All non-profile settings updates flow through
// `.patch`.
import { db } from '@/lib/db';
import type { OverlayConfig } from '@/lib/overlay';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ThemePreference = 'light' | 'dark' | 'system';

export interface UserSettings {
  theme: ThemePreference;
  timezone: string;
  dateFormat: string;
  itemsPerPage: number;
  emailNotifications: boolean;
  /** Field-inspection map: when true the map smoothly follows the user's GPS. */
  mapFollowEnabled: boolean;
  /** Field camera: play a synthesized shutter click on capture. */
  cameraShutterSoundEnabled: boolean;
  /** Field-inspection: silently flush pending uploads on visibility/online. */
  autoSyncEnabled: boolean;
  /** FI#5: per-municipality camera-overlay line config, keyed by muni id. */
  cameraOverlay: Record<string, OverlayConfig>;
  updatedAt: string | null;
}

export interface UpdateProfileRequest {
  displayName: string;
}

export interface UpdateProfileResponse {
  displayName: string;
  message: string;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_SETTINGS: UserSettings = {
  theme: 'system',
  timezone: 'America/Edmonton',
  dateFormat: 'YYYY-MM-DD',
  itemsPerPage: 25,
  emailNotifications: true,
  mapFollowEnabled: false,
  cameraShutterSoundEnabled: true,
  autoSyncEnabled: true,
  cameraOverlay: {},
  updatedAt: null,
};

// ---------------------------------------------------------------------------
// Dexie Cache Helpers
// ---------------------------------------------------------------------------

const CACHED_SETTINGS_KEY = 'userSettings';

async function cacheSettings(settings: UserSettings): Promise<void> {
  try {
    await db.settings.put({
      key: CACHED_SETTINGS_KEY,
      value: { ...settings, cachedAt: Date.now() } as Record<string, unknown>,
    });
  } catch (err) {
    console.warn('Failed to cache settings:', err);
  }
}

async function getCachedSettings(): Promise<UserSettings | null> {
  try {
    const setting = await db.settings.get(CACHED_SETTINGS_KEY);
    if (!setting || typeof setting.value !== 'object' || setting.value === null) {
      return null;
    }
    const cached = setting.value as Record<string, unknown>;
    // Validate required fields exist and theme is a known value
    const validThemes: readonly string[] = ['light', 'dark', 'system'];
    if (typeof cached.theme !== 'string' || !validThemes.includes(cached.theme)) {
      return null;
    }
    return {
      theme: cached.theme as ThemePreference,
      timezone: (cached.timezone as string) ?? DEFAULT_SETTINGS.timezone,
      dateFormat: (cached.dateFormat as string) ?? DEFAULT_SETTINGS.dateFormat,
      itemsPerPage: (cached.itemsPerPage as number) ?? DEFAULT_SETTINGS.itemsPerPage,
      emailNotifications: typeof cached.emailNotifications === 'boolean'
        ? cached.emailNotifications
        : DEFAULT_SETTINGS.emailNotifications,
      mapFollowEnabled: typeof cached.mapFollowEnabled === 'boolean'
        ? cached.mapFollowEnabled
        : DEFAULT_SETTINGS.mapFollowEnabled,
      cameraShutterSoundEnabled: typeof cached.cameraShutterSoundEnabled === 'boolean'
        ? cached.cameraShutterSoundEnabled
        : DEFAULT_SETTINGS.cameraShutterSoundEnabled,
      autoSyncEnabled: typeof cached.autoSyncEnabled === 'boolean'
        ? cached.autoSyncEnabled
        : DEFAULT_SETTINGS.autoSyncEnabled,
      cameraOverlay:
        cached.cameraOverlay && typeof cached.cameraOverlay === 'object'
          ? (cached.cameraOverlay as Record<string, OverlayConfig>)
          : DEFAULT_SETTINGS.cameraOverlay,
      updatedAt: (cached.updatedAt as string | null) ?? null,
    };
  } catch (err) {
    console.warn('Failed to read cached settings:', err);
    return null;
  }
}

async function clearCachedSettings(): Promise<void> {
  try {
    await db.settings.delete(CACHED_SETTINGS_KEY);
  } catch (err) {
    console.warn('Failed to clear cached settings:', err);
  }
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Settings service with AbortController support for all async operations
 */
export const settingsService = {
  /**
   * Fetch user settings from the API.
   * Falls back to Dexie cache on failure. If the cache also misses, rethrows
   * the original error instead of resolving to DEFAULT_SETTINGS — a fetch
   * failure with nothing cached is a real error, not "the user's settings
   * are factory defaults," and callers (useSettings) need the rejection to
   * surface their error/Retry UI rather than silently rendering defaults as
   * saved settings.
   */
  get: async (options?: { signal?: AbortSignal }): Promise<UserSettings> => {
    try {
      const data = await get<UserSettings>('/settings', {
        signal: options?.signal,
      });
      // Cache for offline use
      await cacheSettings(data);
      return data;
    } catch (err) {
      // Try cache on API failure (offline mode) — cached values are real
      // previously-saved data, so this fallback is safe to resolve with.
      const cached = await getCachedSettings();
      if (cached) {
        return cached;
      }
      // No cache to fall back to: propagate the original failure.
      throw err;
    }
  },

  /**
   * Partial update of user settings (PATCH /settings)
   */
  patch: async (
    fields: Partial<UserSettings>,
    options?: { signal?: AbortSignal }
  ): Promise<UserSettings> => {
    const data = await patch<UserSettings>('/settings', fields, {
      signal: options?.signal,
    });
    await cacheSettings(data);
    return data;
  },

  /**
   * Update user profile (display name, email) via PUT /settings/profile
   */
  updateProfile: async (
    profileData: UpdateProfileRequest,
    options?: { signal?: AbortSignal }
  ): Promise<UpdateProfileResponse> => {
    return put<UpdateProfileResponse>('/settings/profile', profileData, {
      signal: options?.signal,
    });
  },

  /**
   * Clear the Dexie settings cache
   */
  clearCache: clearCachedSettings,

  /**
   * Get settings from Dexie cache only (for synchronous-like theme init).
   * Returns null if not cached.
   */
  getCached: getCachedSettings,
};

export default settingsService;
